import axios from 'axios';
import { getKjederegAdminApiUrl } from 'services';

// RAW TYPES (reflect the API response exactly)
export interface BrregInfoUpdateRaw {
  typeId: string;
  createdAt: string; // ISO
  update: {
    orgNr: string;
    juridiskNavn: string | null;
    propertyName: string;
    oldValue: string | null;
    newValue: string | null;
    activeEnhetCount: number;
    hovedkontorEnhetId: number | null;
    actionLog: string[] | null;
    updatedAt: string; // ISO
    antallEnheterAutomaticallyUpdated?: number; // optional count of units updated in this change (if derivable)
    antallEnheterSentToQA?: number;
  };
  id: string;
  partitionKey: string;
  _ts: number;
  ttl: number;
  _etag: string;
  typeName: string;
  environment: string;
}

export interface RawSearchResponse {
  total: number;
  page: number;
  pageSize: number;
  items: BrregInfoUpdateRaw[];
}

// FLATTENED DTO FOR UI (keeps page component unchanged)
export interface BrregInfoUpdateDto {
  id: string;
  orgNr: string;
  field: string; // propertyName
  oldValue: string | null;
  newValue: string | null;
  activeEnhetCount: number;
  hovedkontorEnhetId: number | null;
  actionLog: string[] | null;
  changedAtUtc: string; // update.updatedAt
  source?: string | null; // mapped from environment
  typeId?: string;
  createdAt?: string;
  juridiskNavn?: string | null; // optional legal name (if provided or derivable)
  antallEnheterAutomaticallyUpdated?: number; // optional count of units updated in this change (if derivable)
  antallEnheterSentToQA?: number; // optional count of units sent to QA in this change (if derivable)
  raw?: BrregInfoUpdateRaw; // optional raw access for advanced use
}

export interface SearchResponse {
  page: number;
  pageSize: number;
  total: number;
  items: BrregInfoUpdateDto[];
}

export interface SearchParams {
  page?: number;
  pageSize?: number;
  orgNr?: string;
  value?: string; // free text over newValue/oldValue server-side
  propertyName?: string; // filter by property name (felttype)
}

// Mirrors C# record BrregInfoUpdateMessage
export interface BrregInfoUpdateMessage {
  orgNr: string;
  juridiskNavn?: string | null;
  propertyName: string;
  oldValue?: string | null;
  newValue?: string | null;
}

const baseUrl = getKjederegAdminApiUrl() + '/BrregInfoUpdates';

export async function searchBrregUpdates(params: SearchParams): Promise<SearchResponse> {
  const { page = 1, pageSize = 50, orgNr, value, propertyName } = params;
  const url = new URL(`${baseUrl}/search`);
  url.searchParams.set('page', String(page));
  url.searchParams.set('pageSize', String(pageSize));
  if (orgNr) url.searchParams.set('orgNr', orgNr.trim());
  if (value) url.searchParams.set('value', value.trim());
  if (propertyName) url.searchParams.set('propertyName', propertyName.trim());

  const response = await axios.get<RawSearchResponse>(url.toString());
  const raw = response.data;
  const items: BrregInfoUpdateDto[] = (raw.items || []).map((doc) => ({
    id: doc.id,
    orgNr: doc.update?.orgNr ?? '',
    field: doc.update?.propertyName ?? '',
    oldValue: doc.update?.oldValue ?? null,
    newValue: doc.update?.newValue ?? null,
    changedAtUtc: doc.update?.updatedAt ?? doc.createdAt,
    source: doc.environment || undefined,
    typeId: doc.typeId,
    createdAt: doc.createdAt,
    juridiskNavn: doc.update?.juridiskNavn ?? null,
    activeEnhetCount: doc.update?.activeEnhetCount ?? 0,
    hovedkontorEnhetId: doc.update?.hovedkontorEnhetId ?? null,
    actionLog: doc.update?.actionLog ?? null,
    antallEnheterAutomaticallyUpdated: doc.update?.antallEnheterAutomaticallyUpdated ?? 0,
    antallEnheterSentToQA: doc.update?.antallEnheterSentToQA ?? 0,
    raw: doc,
  }));
  return {
    page: raw.page,
    pageSize: raw.pageSize,
    total: raw.total,
    items,
  };
}

// Publish/resend a stored Brreg update back to the message queue
export async function resendBrregUpdate(msg: BrregInfoUpdateMessage): Promise<void> {
  const url = new URL(`https://ts-snitch.azurewebsites.net/brreg-updater/publish-change`);
  // Primary expected endpoint
  try {
    await axios.post(url.toString(), msg);
    return;
  } catch (e: any) {
    // Fallbacks for compatibility if backend uses a different route name
    if (e?.response?.status === 404 || e?.response?.status === 405) {
      try {
        await axios.post(url.toString(), msg);
        return;
      } catch (_) {
        // try another common alias
        await axios.post(url.toString(), msg);
        return;
      }
    }
    throw e;
  }
}

/**
 * Trigger Brreg update for one or more organization numbers.
 * This will fetch fresh data from Brreg and process any changes.
 * @param orgNrs - Array of organization numbers to trigger updates for
 */
export async function triggerBrregUpdate(orgNrs: string[]): Promise<void> {
  const url = `${getKjederegAdminApiUrl()}/brregsync/dashboard/jobs/brreg/trigger-update`;
  await axios.post(url, { orgNrs });
}

// --- Brreg Jobs Dashboard (proxied via KjederegisteretApi) ---

export interface BrregJobsDashboardResponse {
  checkedAt: string; // ISO
  jobs: BrregJobSummary[];
}

export interface BrregJobSummary {
  jobName: string;
  effectiveSettings: {
    enabled?: boolean;
    lookbackDays?: number;
    pageSize?: number;
    cursorMode?: string | null;
    lastOppdateringsId?: number | null;
    [key: string]: any;
  };
  overrides: {
    enabledOverride?: boolean | null;
    lookbackDaysOverride?: number | null;
    pageSizeOverride?: number | null;
    cursorModeOverride?: string | null;
    lastOppdateringsIdOverride?: number | null;
    [key: string]: any;
  };
  lastRun?: {
    succeeded?: boolean;
    startedAt?: string | null;
    finishedAt?: string | null;
    fetchedCount?: number;
    matchedCount?: number;
    autoUpdatedCount?: number;
    qaMessagesSentCount?: number;
    [key: string]: any;
  } | null;
  hoursSinceLastSuccess?: number | null;
}

export async function getBrregJobsDashboard(): Promise<BrregJobsDashboardResponse> {
  const url = `${getKjederegAdminApiUrl()}/brregsync/dashboard/jobs`;
  const response = await axios.get<BrregJobsDashboardResponse>(url);
  return response.data;
}

export type BrregJobName =
  | 'brreg-enhet-updates'
  | 'brreg-role-updates'
  | 'brreg-underenhet-updates'
  | 'brreg-nye-enheter-nace';

export async function runBrregJobNow(jobName: BrregJobName, minDato?: string): Promise<void> {
  const url = `${getKjederegAdminApiUrl()}/brregsync/dashboard/jobs/${jobName}/run-now`;
  await axios.post(url, minDato ? { minDato } : undefined);
}

export interface BrregJobRunLogEntry {
  orgNr: string;
  juridiskNavn?: string | null;
  action: string;
  detail?: string | null;
  naeringskode1?: string | null;
  naeringskode2?: string | null;
  organisasjonsform?: string | null;
  timestamp: string;
  brregEventTime?: string | null;
}

export interface BrregJobRunDto {
  id?: string | null;
  jobName: string;
  startedAt?: string | null;
  finishedAt?: string | null;
  succeeded?: boolean | null;
  outcome?: string | null;
  errorMessage?: string | null;
  cursorFrom?: string | null;
  cursorTo?: string | null;
  maxOppdateringsIdSeen?: number | null;
  effectiveLookbackDays?: number | null;
  effectivePageSize?: number | null;
  fetchedCount?: number | null;
  matchedCount?: number | null;
  processedCount?: number | null;
  skippedCount?: number | null;
  errorCount?: number | null;
  qaMessagesSentCount?: number | null;
  autoUpdatedCount?: number | null;
  logEntries?: BrregJobRunLogEntry[] | null;
  logEntriesTruncated?: boolean | null;
}

export async function getJobRuns(jobName: string, take = 50): Promise<BrregJobRunDto[]> {
  const url = `${getKjederegAdminApiUrl()}/brregsync/dashboard/jobs/${encodeURIComponent(jobName)}/runs?take=${take}`;
  const response = await axios.get<{ jobName: string; take: number; runs: BrregJobRunDto[] }>(url);
  return response.data.runs;
}

export interface BrregEnhetOutcomeEntry {
  orgNr: string;
  juridiskNavn?: string | null;
  action: string;
  detail?: string | null;
  propertyName: string;
  brregEventTime?: string | null;
  timestamp: string;
}

export interface BrregEnhetOutcomesDto {
  runId: string;
  jobName: string;
  autoUpdatedCount: number;
  qaSentCount: number;
  errorCount: number;
  enqueuedCount: number;
  entries: BrregEnhetOutcomeEntry[];
}

export async function getJobRunOutcomes(
  jobName: string,
  runId: string,
): Promise<BrregEnhetOutcomesDto> {
  const url = `${getKjederegAdminApiUrl()}/brregsync/dashboard/jobs/${encodeURIComponent(jobName)}/runs/${encodeURIComponent(runId)}/outcomes`;
  const response = await axios.get<BrregEnhetOutcomesDto>(url);
  return response.data;
}

// --- Nye underenheter - latest run result ---

export interface QaRunCandidate {
  bedriftsnr: string;
  enhetsnavn?: string | null;
  parentOrgnr?: string | null;
  parentNavn?: string | null;
  bransjeForslag?: string | null;
  besokGateadresse?: string | null;
  besokPostnummer?: string | null;
  besokPoststed?: string | null;
  postGateadresse?: string | null;
  postPostnummer?: string | null;
  postPoststed?: string | null;
  epost?: string | null;
  telefon?: string | null;
  hjemmeside?: string | null;
}

export interface LatestUnderenhetUpdatesResult {
  runAt: string; // ISO
  triggeredBy?: string | null;
  fetchedCount: number;
  nyCount: number;
  errorCount: number;
  qaCandidateCount: number;
  qaCandidates: QaRunCandidate[];
}

export async function getLatestUnderenhetResult(): Promise<LatestUnderenhetUpdatesResult> {
  const url = `${getKjederegAdminApiUrl()}/brregsync/dashboard/jobs/underenhet-updates/latest-result`;
  const response = await axios.get<LatestUnderenhetUpdatesResult>(url);
  return response.data;
}
